<template>
  <HrPageLayout
    title="My Clearance Status"
    subtitle="Track where your exit clearance currently is"
    icon="📋"
    header-tone="institutional"
    :breadcrumb-items="[
      { label: 'Self Service', route: '/employee-dashboard' },
      { label: 'My Clearance Status' }
    ]"
  >
    <div v-if="loading" class="clr-empty">Loading…</div>
    <div v-else-if="!cases.length" class="clr-card">
      <p class="clr-muted">No clearance case is open for your staff record yet. When you are on terminal leave / exit processing, HR opens a case and you can track stages here (and by email).</p>
    </div>
    <div v-else class="clr-stack">
      <div v-for="c in cases" :key="c.id" class="clr-card">
        <div class="clr-head">
          <div>
            <h3 class="mono">{{ c.clearance_number }}</h3>
            <div class="sub">{{ formatExit(c.exit_type) }} · {{ formatStatus(c.overall_status) }}</div>
          </div>
          <span class="badge warn">Current: {{ c.current_stage_name || '—' }}</span>
        </div>
        <p v-if="c.certificate_number" class="ok">Certificate: {{ c.certificate_number }}</p>
        <ol class="stage-list">
          <li
            v-for="s in (c.stages || [])"
            :key="s.id"
            :class="stageClass(s, c)"
          >
            <strong>{{ s.stage_name }}</strong>
            <span>{{ formatStatus(s.status) }}</span>
            <span v-if="s.acted_by" class="sub">Acted by {{ s.acted_by }}</span>
            <span v-if="s.due_at && isActive(s)" class="sub">Due {{ s.due_at }}</span>
          </li>
        </ol>
        <div v-if="(c.notifications || []).length" class="notes">
          <h4>Updates</h4>
          <ul>
            <li v-for="(n, i) in c.notifications" :key="i">
              <span class="sub">{{ n.created_at }}</span> — {{ n.message }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "MyClearanceStatus",
  components: { HrPageLayout },
  data() {
    return { loading: false, cases: [] };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "clrGetOwnStatus");
        const res = await execute(fd, authUrl);
        this.cases = res.data?.success ? res.data.data?.cases || [] : [];
      } catch (_) {
        this.cases = [];
      } finally {
        this.loading = false;
      }
    },
    formatStatus(s) {
      return String(s || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatExit(s) {
      return this.formatStatus(s);
    },
    isActive(s) {
      return ["pending", "in_review", "returned"].includes(String(s.status || "").toLowerCase());
    },
    stageClass(s, c) {
      const st = String(s.status || "").toLowerCase();
      const current = String(c.current_stage_code || "") === String(s.stage_code || "");
      return {
        done: ["cleared", "cleared_with_liability", "not_applicable"].includes(st),
        active: current || this.isActive(s),
        returned: st === "returned"
      };
    }
  }
};
</script>

<style scoped>
.clr-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
}
.clr-stack { display: flex; flex-direction: column; gap: 12px; }
.clr-head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.clr-head h3 { margin: 0; color: #0c2d48; }
.mono { font-variant-numeric: tabular-nums; }
.sub { font-size: 12px; color: #64748b; }
.clr-muted { color: #64748b; }
.clr-empty { text-align: center; padding: 40px; color: #64748b; }
.badge {
  display: inline-block; padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700;
}
.badge.warn { background: #fff7ed; color: #c2410c; }
.ok { color: #047857; font-weight: 700; }
.stage-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
.stage-list li {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px;
  display: grid; gap: 2px; background: #f8fafc;
}
.stage-list li.done { background: #ecfdf5; border-color: #86efac; }
.stage-list li.active { background: #eff6ff; border-color: #93c5fd; font-weight: 600; }
.stage-list li.returned { background: #fff7ed; border-color: #fdba74; }
.notes { margin-top: 14px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
.notes h4 { margin: 0 0 8px; font-size: 14px; }
.notes ul { margin: 0; padding-left: 18px; color: #334155; font-size: 13px; }
</style>
